import { useEffect } from 'react';
import { Link } from 'react-router-dom';

import { Figure, Flow } from '../components/Diagram';
import { UiPath } from '../components/UiPath';

type Row = { k: string; v: string };

const FIELDS: Row[] = [
  { k: 'Task name', v: 'Required. Everything else is optional.' },
  { k: 'Description', v: 'A rich text box for what needs to happen, and why.' },
  { k: 'Priority', v: 'Low, Normal, High or Urgent.' },
  { k: 'Due date', v: 'A calendar day. When you set Repeat, the field becomes First task.' },
  {
    k: 'Repeat',
    v: 'Doesn’t repeat, Every day, Every week, Every month, Every 3 months, or Custom. A job that runs every 30 minutes creates the next task of a series.',
  },
  {
    k: 'Assignees',
    v: 'One or more teammates. Revvly sends no message when you assign a task, so tell the person or have them look under My tasks.',
  },
  { k: 'Related to', v: 'A company, contact or deal. See step 03.' },
];

const VIEWS: Row[] = [
  {
    k: 'Table',
    v: 'The default. Open tasks first, closed tasks in a second section. Open starts on Needs attention (overdue plus due today); change the date filter to All dates for the rest. Closed shows only what you closed today until you turn on Show closed.',
  },
  {
    k: 'Board',
    v: 'Columns for To do, In progress and Done. Drag a card to change its status. It includes open tasks due later and tasks with no date.',
  },
  {
    k: 'Due date',
    v: 'Columns for Past due, Today and Tomorrow. Drag a card onto Today or Tomorrow to reschedule it. Past due is not a drop target because it has no single date.',
  },
];

const QUEUE_ACTIONS: Row[] = [
  {
    k: 'Email / Write email',
    v: 'Opens the composer to the task’s contact (the button names them when the task does). Sending completes the task and creates the follow-up if you added one. Needs a company on the task.',
  },
  { k: 'Log note', v: 'Saves a note on the related record and completes the task. Only workspaces with CRM see it.' },
  { k: 'Mark done', v: 'Completes the task without sending anything.' },
  { k: 'Skip to next', v: 'Moves on without changing the task. A skipped task stays in the queue and comes round again.' },
  {
    k: 'Snooze',
    v: 'Quick picks of Tomorrow, In 3 days, In 1 week and In 2 weeks, or Days from now (1 to 365), or a date under Or on a date. It opens on the next Monday to Friday day. The due date becomes that day and the task leaves the queue until then.',
  },
];

const COMPOSER: Row[] = [
  {
    k: 'Follow-up task',
    v: 'Choose Add follow-up under the editor, then set Follow up in a number of business days from 1 to 90. It is on by default for companies that are not clients yet and off for clients. The task is named “Follow up with” the recipient, assigned to you, and linked to the contact you wrote to (the company when there is none). It is created after the send; if that step fails the email still goes and a message says so.',
  },
  {
    k: 'Your default delay',
    v: 'Each person sets a starting number in the Email follow-up card, under Due in. The initial value is 3. You can change it on every send.',
  },
  {
    k: 'Business days',
    v: 'Counted from tomorrow on the workspace’s working days and timezone, and the result is always a working day. Changing working days affects follow-ups created afterwards, not existing ones.',
  },
  {
    k: 'Close a task on send',
    v: 'If you have an open task on that account, the Send button reads Send email and close task, and the arrow beside it offers Send email only. It offers one task: your most urgent open one by due date, across the company, its contacts and its deals. The confirmation says which task was closed, or that it could not be.',
  },
];

const SYSTEM: Row[] = [
  { k: 'Enter metered usage', v: 'A manual-capture period closed with nothing entered. Kind: Manual usage. Priority Normal.' },
  { k: 'Unverified metered usage', v: 'An API-capture period closed with no reading. Kind: Unverified usage. Priority High.' },
  { k: 'Subscription has not gone live', v: 'A signed subscription passed its activation window without being activated. It states the amount that is sold and not yet earning.' },
];

function Rows({ rows }: { rows: Row[] }) {
  return (
    <div className="dp-fields-table" style={{ marginTop: 18 }}>
      {rows.map((r) => (
        <div className="dp-field-row" key={r.k}>
          <div className="dp-field-k">{r.k}</div>
          <div className="dp-field-v">{r.v}</div>
        </div>
      ))}
    </div>
  );
}

export function TasksGuide() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Work your tasks — Revvly Docs';
  }, []);

  return (
    <div className="tn-page">
      <nav className="dp-crumbs" aria-label="Breadcrumb">
        <Link to="/docs/guides">Guides</Link>
        <span aria-hidden="true">›</span>
        <span className="dp-crumb-current">Tasks</span>
      </nav>

      <header className="dp-header">
        <span className="rv-eyebrow-mini">Guide</span>
        <h1>
          Work your <em>tasks</em>.
        </h1>
        <p className="dp-oneliner">
          Tasks are the follow-ups attached to your companies, contacts and deals. Read them as a
          table or a board, clear the day in the Work queue, and let a sent email create the next
          step. This page also says plainly what a task reminder does today.
        </p>
      </header>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">01</span>
          Create a task
        </h2>
        <p className="dp-plain">
          Tasks are available to every workspace and every member, with no feature flag. Without CRM
          a task can be linked to a company only; with CRM it can also be linked to a contact or a
          deal. A task has one of four statuses: To do, In progress, Done or Cancelled. Open its
          panel to change the status, archive it or delete it.
        </p>
        <UiPath steps={['Tasks', 'New task']} note="Tasks is in the Workspace group of the sidebar." />
        <Rows rows={FIELDS} />
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">02</span>
          Read the list
        </h2>
        <p className="dp-plain">
          Switch views with the Table, Board and Due date control above the list. Your choice is
          remembered for your user, and the address carries it as{' '}
          <span className="api-inline">?view=</span>, so a link opens the same view. All tasks and My
          tasks sit beside it; Show closed and Show archived are switches.
        </p>
        <Rows rows={VIEWS} />
        <ul className="dp-connects" style={{ marginTop: 18 }}>
          <li>
            Lists load 50 tasks at a time. When there are more, a Load more button under the
            section shows how many you have of the total. It is a button, not infinite scroll.
          </li>
          <li>
            Search, priority and kind filters apply to every view; the date and status filters to
            the table only. Tick tasks to Archive, Restore or Delete them in bulk.
          </li>
        </ul>
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">03</span>
          Link a task to a company, contact or deal
        </h2>
        <p className="dp-plain">
          In Related to, search and pick a record. The list is grouped as Leads, Companies and
          Contacts, and a deal shows “(lead)” after its name because the CRM stores deals as leads.
          Companies load 200 at a time, A to Z, and typing searches all of them on the server.
        </p>
        <p className="dp-plain" style={{ marginTop: 18 }}>
          On a client or company, the Tasks tab lists every task for that account, including tasks
          filed on its contacts and deals, because an email follow-up is filed on the contact you
          wrote to. Tick the box on a row to complete or reopen it.
        </p>
        <UiPath steps={['Clients', 'open a client', 'Tasks']} />
        <p className="dp-plain" style={{ marginTop: 18 }}>
          A deal opens in one slide-out panel with three tabs: Deal, Conversation and Tasks. Deal
          holds the stage buttons and the deal fields. Conversation is the email thread with the
          company, with the composer. Tasks is the same tab as on the company record, so a task
          added there lands on the record too. The panel opens from the pipeline board and the
          forecast table, and needs the CRM flag. See <Link to="/docs/guides/crm">the CRM guide</Link>.
        </p>
        <UiPath steps={['Pipeline', 'open a deal']} />
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">04</span>
          Clear the day in the Work queue
        </h2>
        <p className="dp-plain">
          The Work queue takes one task at a time with the client beside it. It shows your tasks
          only: open, not archived, assigned to you, and overdue or due today. Later and undated
          tasks stay out. Order is group first (Overdue, then Due today), then priority from Urgent
          down, then the oldest due date. A Completed today list and a progress count sit with it.
        </p>
        <UiPath steps={['Work queue']} note="Wide screens show the queue, the task and the account side by side." />
        <Rows rows={QUEUE_ACTIONS} />
        <p className="dp-plain" style={{ marginTop: 18 }}>
          Every action shows on screen at once, then is held for 6 seconds behind an Undo banner
          before it is saved. A send is held the same way, so Undo never has to recall an email.
          Close the tab while an action is waiting and the browser asks you to confirm.
        </p>
        <Figure
          title="One task in the Work queue"
          caption="Every action waits six seconds behind Undo before it is saved."
        >
          <Flow
            steps={[
              { title: 'Your tasks', text: 'Open, not archived, assigned to you.' },
              { title: 'Overdue or due today', text: 'Later and undated tasks stay out.' },
              { title: 'Ordered', text: 'Overdue first, then priority, then oldest due date.' },
              {
                fork: [
                  { title: 'Email, log note or mark done', text: 'Completes the task.', tone: 'accent' },
                  { title: 'Skip to next', text: 'Unchanged. It comes round again.', tone: 'muted' },
                  { title: 'Snooze', text: 'Due date moves. It leaves the queue until then.', tone: 'warn' },
                ],
              },
              { title: 'Held for 6 seconds', text: 'Undo banner, then saved.' },
            ]}
          />
        </Figure>
        <p className="dp-plain" style={{ marginTop: 18 }}>
          The account column lists Open tasks on this account: every open task on the client, its
          contacts and its deals, whoever it is assigned to, up to the first 50. Tick the circle to
          complete one without leaving the queue. A task in your queue opens in the workspace;
          anyone else’s opens in a side panel.
        </p>
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">05</span>
          Snooze and reminders: what is delivered today
        </h2>
        <p className="dp-plain">
          Snooze stores a reminder time of 09:00 your local time on the day you chose. A job runs
          every minute, finds tasks whose reminder time has passed and that are still To do or In
          progress, marks the reminder sent and raises an internal event.
        </p>
        <div className="api-note" style={{ marginTop: 18 }}>
          <span className="api-note-icon" aria-hidden="true">
            i
          </span>
          <div>
            Nothing reaches a person from that event today. A task reminder sends no email, no push
            and no in-app notification. A snoozed task comes back because its new due date arrives,
            and you see it in the Work queue that day. Only metered-usage tasks react to the event,
            and what they do is raise their own priority (step 07).
          </div>
        </div>
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">06</span>
          Create and close tasks from the email composer
        </h2>
        <p className="dp-plain">
          Two things can happen as you send from a client or company conversation, or from a
          contact’s Send email panel. An admin sets the working days on Settings, General; see{' '}
          <Link to="/docs/guides/workspace-settings">Configure your workspace</Link>.
        </p>
        <Rows rows={COMPOSER} />
        <UiPath steps={['Account', 'Email follow-up']} note="Your personal default delay." />
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">07</span>
          Tasks Revvly raises for you
        </h2>
        <p className="dp-plain">
          Some tasks are created by the system so that missing work is visible. They are assigned to
          every active member who has accepted their invitation, and they close themselves when the
          cause is resolved.
        </p>
        <Rows rows={SYSTEM} />
        <ul className="dp-connects" style={{ marginTop: 18 }}>
          <li>
            One open usage task exists per subscription and period, due on the period end. It
            closes when every reading for the period is sealed or waived, and five days after the
            due date the reminder job raises it to Urgent.
          </li>
          <li>
            The task panel links to Client Settings and Bulk entry, where you enter the quantity
            or waive the reading. Filter by Kind: Metered usage (all), Manual usage or Unverified
            usage.
          </li>
        </ul>
        <p className="dp-plain" style={{ marginTop: 18 }}>
          See <Link to="/docs/guides/metered-usage">Metered usage</Link> for capture modes and
          sealing, and <Link to="/docs/guides/billing-schedules">Billing schedules</Link> for
          activation.
        </p>
      </section>
    </div>
  );
}
